import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Zaptec Go 2 vs Easee Peak — bespoke rich comparison.
 * Geo-aware. Registered for zaptec-go-2-vs-easee-peak + regional variants.
 * Sources: Zaptec Go 2 + Easee Peak official specs. No competitor electricity pricing.
 */
export default function ZaptecGo2VsEaseePeak({ page }) {
  const geo = page?.geography;
  const place = geo?.name ? ` i ${geo.name}` : '';
  const zone = geo?.zone;
  const zoneLabel = zone === 'DK1' ? 'DK1 (Vestdanmark)' : zone === 'DK2' ? 'DK2 (Østdanmark)' : null;

  const go2 = ENKEL_PRODUCTS.chargers.zaptec_go_2;

  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Hvad er forskellen på Zaptec Go 2 og Easee Peak?',
      answer: 'Begge er 22 kW Type 2-ladebokse. Zaptec Go 2 er en hjemmelader med indbygget skærm, MID-certificeret måler, ISO 15118 og V2G-forberedelse. Easee Peak er Easees model rettet mod erhverv og delte/offentlige installationer med fokus på flere ladepunkter og dynamisk effektdeling. Til et privat hjem er Zaptec Go 2 typisk det mest oplagte valg.',
    },
    {
      question: 'Er Zaptec Go 2 V2G-klar?',
      answer: 'Ja. Zaptec Go 2 er forberedt til V2G (tovejsopladning), så bilen på sigt kan sende strøm tilbage til hjem eller net, når dansk lovgivning og elselskabsaftaler er på plads (ventes 2026–2027). Easee Peak er ikke markedsført som V2G-ready.',
    },
    {
      question: 'Kan jeg bruge en Easee Peak med enkel energi?',
      answer: 'Ja. Vi sælger ikke Easee Peak, men enkel Saving Mode og laderabat (10 øre/kWh) er kompatible med Easee-modeller. Har du en Easee installeret, tilslutter du den blot til enkel-appen.',
    },
    {
      question: 'Hvad koster Zaptec Go 2 hos enkel?',
      answer: `Zaptec Go 2 koster ${go2.price.toLocaleString('da-DK')} kr. Prisen inkluderer Saving Mode, serviceaftale og 10 øre/kWh laderabat til 0 kr./md. Installation kommer oveni fra 1.300 kr.`,
    },
    {
      question: 'Hvilken passer bedst til et privat hjem?',
      answer: 'Til et almindeligt hjem med én elbil er Zaptec Go 2 oftest det rigtige valg: den har alt du behøver, er fremtidssikret med V2G, og inkluderer Saving Mode. Easee Peak giver primært mening, hvor flere ladepunkter skal dele effekt – fx erhverv, boligforeninger eller fælles parkering.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'tabel', label: 'Side-om-side' },
    { id: 'go2', label: 'Zaptec Go 2' },
    { id: 'peak', label: 'Easee Peak' },
    { id: 'anbefaling', label: 'Anbefaling' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: `Zaptec Go 2 vs Easee Peak${place}`, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={`Zaptec Go 2 vs Easee Peak${place}`} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Sammenligning · Ladebokse${zoneLabel ? ' · ' + zone : ''} · 2026`}
        h1={`Zaptec Go 2 vs Easee Peak${place}`}
        intro={`En premium hjemmelader mod en erhvervs-orienteret ladeboks. Vi gennemgår forskellene${place} – fra måler og V2G til hvornår hver model giver mening.`}
        badges={['Begge 22 kW', 'Type 2-stik', 'Zaptec Go 2 er V2G-ready']}
        updated={updated}
        image="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: `${go2.price.toLocaleString('da-DK')} kr.`, label: 'Zaptec Go 2 (V2G-ready)' },
          { value: '22 kW', label: 'begge modeller' },
          { value: 'MID', label: 'måler i Go 2' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Go 2 eller Easee Peak – kort svar">
        <AnswerBox question="Hvilken skal jeg vælge?">
          Til et <strong>privat hjem</strong> er <strong>Zaptec Go 2</strong> som regel det rigtige valg: indbygget skærm, MID-måler, ISO 15118
          og <strong>V2G-forberedelse</strong>. <strong>Easee Peak</strong> er bygget til <strong>erhverv og delte installationer</strong> med flere
          ladepunkter og dynamisk effektdeling. Begge er 22 kW med Type 2 – så ladehastigheden er ens.
        </AnswerBox>
        {zoneLabel && (
          <p>
            {geo.name} ligger i <strong>{zoneLabel}</strong>. Med enkel Saving Mode lader Zaptec Go 2 automatisk i de billigste timer på Nord Pool
            i {zone}, og du får 10 øre/kWh i laderabat oveni på opladning styret af Saving Mode i enkel-appen.
          </p>
        )}
      </ProseSection>

      <ProseSection id="tabel" heading="Zaptec Go 2 vs Easee Peak – side om side" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Sammenligning af Zaptec Go 2 og Easee Peak"
            columns={[{ name: 'Zaptec Go 2', highlight: true }, { name: 'Easee Peak' }]}
            rows={[
              { label: 'Primær brug', values: ['Hjem', 'Erhverv / delt'] },
              { label: 'Pris (hos enkel)', values: [`${go2.price.toLocaleString('da-DK')} kr.`, 'Sælges ikke'] },
              { label: 'Max effekt', values: ['22 kW', '22 kW'] },
              { label: 'Stiktype', values: ['Type 2', 'Type 2'] },
              { label: 'Indbygget skærm', values: [true, false] },
              { label: 'MID-måler', values: [true, 'Afhænger'] },
              { label: 'ISO 15118 / Plug & Charge', values: [true, false] },
              { label: 'V2G-ready', values: [true, false] },
              { label: 'Effektdeling mellem flere bokse', values: ['Load balancing', 'Ja (kerneformål)'] },
              { label: 'Saving Mode-kompatibel', values: [true, true] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">Kilder: Zaptec Go 2 og Easee Peak officielle produktspecifikationer.</p>
      </ProseSection>

      <div className="container mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div id="go2">
            <ChargerCard charger={go2} note="Premium hjemmelader · V2G-ready" />
            <div className="mt-4">
              <ProsCons
                pros={['Indbygget skærm + MID-måler', 'ISO 15118 (Plug & Charge-klar)', 'V2G-ready – fremtidssikret', 'Saving Mode + service inkl.']}
                cons={['Dyrere end basis-modeller', 'V2G kræver fremtidig lovgivning']}
              />
            </div>
          </div>
          <div id="peak">
            <div className="bg-white rounded-2xl p-5 border border-[#263A43]/12 h-full flex flex-col">
              <h3 className="font-bold text-gray-900 mb-1">Easee Peak</h3>
              <p className="text-xs text-gray-500 mb-3">Easee · op til 22 kW · Type 2 · erhverv</p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4 flex-1">
                Easee Peak er målrettet erhverv og delte installationer, hvor mange ladepunkter skal dele den tilgængelige effekt dynamisk.
                Vi sælger den ikke, men enkel Saving Mode virker med Easee-modeller, hvis du allerede har en installeret.
              </p>
              <div className="mt-2">
                <ProsCons
                  pros={['Stærk til mange ladepunkter', 'Dynamisk effektdeling', 'Virker med enkel Saving Mode']}
                  cons={['Overkill til ét privat hjem', 'Ikke V2G-ready', 'Sælges ikke hos enkel']}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProseSection id="anbefaling" heading="Vores anbefaling" alt>
        <FactGrid columns={2} facts={[
          { value: 'Privat hjem', label: 'Vælg Zaptec Go 2', sub: 'Fremtidssikret med V2G + MID-måler' },
          { value: 'Erhverv / delt', label: 'Easee Peak giver mening', sub: 'Når flere bokse skal dele effekt' },
        ]} />
        <p className="mt-6">
          For langt de fleste boligejere er Zaptec Go 2 det rigtige valg – den har alt du behøver og er klar til fremtidens tovejsopladning.
          Easee Peak er først relevant, når du driver en fælles- eller erhvervsinstallation. Uanset hvad får du Saving Mode, serviceaftale og
          laderabat til 0 kr./md. hos enkel energi.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/zaptec-go-2', label: 'Zaptec Go 2 – produktside' },
        { path: '/sammenlign/zaptec-go-vs-go-2', label: 'Zaptec Go vs Go 2' },
        { path: '/sammenlign/v2g-guide', label: 'V2G-guide' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks skal jeg vælge?' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA
        headline="Vælg din ladeboks"
        subtext="Zaptec Go 2 inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md. – og er klar til V2G."
        primary={{ path: '/zaptec-go-2', text: 'Se Zaptec Go 2' }}
        secondary={{ path: '/opladere', text: 'Se alle ladebokse' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}